import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/shared/lib/axios';
import { AdminLoginCredentials, AdminLoginResponse, Admin } from '@/types/admin-auth';
import {
  clearSsoSession,
  hasPortalCookieSession,
  isSsoSite,
  markPortalAuthChannel,
} from '@/features/sso/ssoSession';
import {
  logoutAdminSite,
  type AdminSiteLogoutResponse,
} from './adminSiteLogout';

// ==================== API Service Functions ====================

/**
 * Login site admin with email, password, and site
 */
const login = async (credentials: AdminLoginCredentials, site: string): Promise<AdminLoginResponse> => {
  const response = await apiClient.post<AdminLoginResponse>(`/admin/${site}/login`, credentials);
  return response.data;
};

/**
 * Logout site admin
 */
const logout = (
  site: string,
  exactBearerToken: string | null,
): Promise<AdminSiteLogoutResponse> =>
  logoutAdminSite(
    site,
    async (logoutSite, bearerToken) => {
      const response = await apiClient.post<AdminSiteLogoutResponse>(
        `/admin/${logoutSite}/logout`,
        undefined,
        bearerToken
          ? { headers: { Authorization: `Bearer ${bearerToken}` } }
          : undefined,
      );
      return response.data;
    },
    localStorage,
    exactBearerToken,
  );

/**
 * Get current site admin info
 */
const getAdmin = async (site: string): Promise<{
  success: boolean;
  data?: {
    admin: Admin;
    sso_establish_url?: string;
  };
}> => {
  const response = await apiClient.get(`/admin/${site}/user`);
  return response.data;
};

/**
 * Refresh site admin token
 */
const refreshToken = async (site: string): Promise<{ success: boolean; data?: { token: string; token_type: string } }> => {
  const response = await apiClient.post(`/admin/${site}/refresh`);
  return response.data;
};

/**
 * Store site admin token
 */
const setToken = (token: string): void => {
  localStorage.setItem('site_admin_token', token);
};

/**
 * Get stored site admin token
 */
const getToken = (): string | null => {
  return localStorage.getItem('site_admin_token');
};

/**
 * Store site admin info
 */
const setAdmin = (admin: Admin): void => {
  localStorage.setItem('site_admin', JSON.stringify(admin));
};

/**
 * Store current site
 */
const setSite = (site: string): void => {
  localStorage.setItem('site_admin_site', site);
};

/**
 * Check if site admin is authenticated
 */
const isAuthenticated = (): boolean => {
  return !!getToken();
};

// ==================== Hooks ====================

/**
 * Hook for site admin login
 */
export const useAdminSiteLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ credentials, site }: { credentials: AdminLoginCredentials; site: string }) =>
      login(credentials, site),
    onSuccess: (data: AdminLoginResponse, variables) => {
      if (data.success && data.data) {
        // Store the exact local bearer state before exposing its non-secret channel marker.
        setToken(data.data.token);
        setAdmin(data.data.admin);
        setSite(variables.site);
        if (isSsoSite(variables.site)) {
          clearSsoSession({ site: variables.site, entryType: 'admin' });
          markPortalAuthChannel(
            { site: variables.site, entryType: 'admin' },
            'bearer',
            localStorage
          );
        }
        // Invalidate admin query
        queryClient.invalidateQueries({ queryKey: ['admin', 'site', 'user'] });
      }
    },
  });
};

/**
 * Hook for site admin logout
 */
export const useAdminSiteLogout = () => {
  return useMutation({
    mutationFn: ({
      site,
      exactBearerToken,
    }: {
      site: string;
      exactBearerToken: string | null;
    }) => logout(site, exactBearerToken),
  });
};

/**
 * Hook to get current site admin info
 */
export const useAdminSiteGetUser = (site: string) => {
  const hasCookieSession =
    isSsoSite(site) &&
    hasPortalCookieSession(
      { site, entryType: 'admin' },
      localStorage,
      sessionStorage
    );

  return useQuery({
    queryKey: ['admin', 'site', 'user', site],
    queryFn: () => getAdmin(site),
    enabled: !!site && (isAuthenticated() || hasCookieSession),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to refresh site admin token
 */
export const useAdminSiteRefreshToken = () => {
  return useMutation({
    mutationFn: (site: string) => refreshToken(site),
    onSuccess: (data) => {
      if (data.success && data.data?.token) {
        setToken(data.data.token);
      }
    },
  });
};
